import {
  formatGbp,
  linesWithCode,
  profitOrLossLabel,
  profitOrLossText,
  type TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { Icon } from '@/kit/Icon';
import { displayDate } from './trialBalanceEditor';

interface TrialBalanceOverviewProps {
  model: TrialBalanceEditModel | null;
  /**
   * Changes whenever the shared model changes. The model object is mutated in place, so the parent re-renders this
   * panel by bumping it (Angular `ngOnChanges`); it is only read for that.
   */
  revision?: number;
  /** APT354: Import Balances sits beside the Period (Overview only; Edit Details has no import). */
  onImportBalances?: () => void;
}

/**
 * Angular TrialBalanceOverviewComponent: the read-only summary tab (the figures are TrialBalanceSummary, fixed top-right - APT347).
 * APT354: the Description joins Client / Reference / Period here (it no longer repeats under the tabs), and Import Balances is
 * beside the Period.
 */
export function TrialBalanceOverview({ model, onImportBalances }: TrialBalanceOverviewProps) {
  let periodText = '';
  if (model) {
    const from = displayDate(model.fromDate);
    const to = displayDate(model.toDate);
    periodText = model.periodLabel || (from && to ? `${from} - ${to}` : '');
  }

  return (
    <>
      <div className="mb-1">
        <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
          <div className="w-100">Client :</div>
          <div>{model?.clientName}</div>
        </div>
        <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
          <div className="w-100">Reference :</div>
          <div>{model?.refNo}</div>
        </div>
        <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
          <div className="w-100">Description :</div>
          <div>{model?.description || '-'}</div>
        </div>
        <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
          <div className="w-100">Period :</div>
          <div>{periodText}</div>
          {onImportBalances && (
            <a className="link text-underline-none d-flex align-items-center" onClick={onImportBalances}>
              <Icon name="file_download" className="font-size-18" />
              Import Balances
            </a>
          )}
        </div>
      </div>
      <div className="text-gray font-size-12 pt-5">The trial balance lines are entered in the grid below.</div>
    </>
  );
}

/**
 * APT347 (client): "Move the Total Debit, Total Credit, and Balance summary to a fixed position in the top-right corner of the Add Trial
 * Balance screen. Use the freed-up space to show more Trial Balance account rows". The same lines, wording and figures the Overview
 * showed (Lines, Total Dr., Total Cr., Profit or Loss, Status), in a compact panel the editor pins to the top-right of its page.
 */
export function TrialBalanceSummary({ model }: TrialBalanceOverviewProps) {
  const lineCount = model ? linesWithCode(model).length : 0;
  const totalDebitText = model ? formatGbp(model.totalDebit) : formatGbp(0);
  const totalCreditText = model ? formatGbp(model.totalCredit) : formatGbp(0);
  const profitLabel = model ? profitOrLossLabel(model.profit) : 'Profit';
  const profitText = model ? profitOrLossText(model.profit) : formatGbp(0);
  // Ticket "Saving TB": the totals are no longer repeated under the lines, so the side and size of a difference shows here.
  const difference = model ? model.totalDebit - model.totalCredit : 0;
  const differenceText = `${difference > 0 ? 'Debit' : 'Credit'} ${formatGbp(Math.abs(difference))}`;

  return (
    <div className="tb-summary" data-testid="tb-summary">
      <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
        <div className="w-100">Lines :</div>
        <div>{lineCount}</div>
      </div>
      <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
        <div className="w-100">Total Dr. :</div>
        <div>{totalDebitText}</div>
      </div>
      <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
        <div className="w-100">Total Cr. :</div>
        <div>{totalCreditText}</div>
      </div>
      <div className="d-flex align-items-center gap-20 text-gray pt-5 pb-5">
        <div className="w-100">{profitLabel} :</div>
        <div>{profitText}</div>
      </div>
      <div className="d-flex align-items-center gap-20 pt-5 pb-5">
        <div className="w-100 text-gray">Status :</div>
        <div className="d-flex gap-5">
          <span className={model?.isBalanced ? 'text-green' : 'text-red'}>
            {model?.isBalanced ? 'Balanced' : 'Out of balance'}
          </span>
          {model && !model.isBalanced && <span className="text-red">{differenceText}</span>}
        </div>
      </div>
    </div>
  );
}
